import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { validateCheckout } from "@/shared/checkoutValidation";
import { filterPaymentMethods } from "@/shared/checkout/paymentMethodRules";
import { nifFromSelectedAddress } from "@/shared/checkout/addressDefaults";
import NewOrderHeader from "./NewOrderHeader";
import useCart from "./useCart";
import useAddresses from "./useAddresses";
import usePaymentMethods from "./usePaymentMethods";
import useCheckoutVerify from "./useCheckoutVerify";
import useDiscounts from "./useDiscounts";
import useNewOrderState, { maxStep } from "./useNewOrderState";
import DiscountsSection from "./DiscountsSection";
import PaymentSection from "./PaymentSection";
import TotalBox from "./TotalBox";
import OrderConfirmedScreen from "./OrderConfirmedScreen";
import { validateMbwayPhone } from "@/utils/mbwayValidation";

function validateExtraCheckout({ isInstallments, parcelas, maxParcelas, isPhonePayment, mbwayPhone }) {
  if (isInstallments) {
    const n = Number(parcelas || 0);
    if (!Number.isInteger(n) || n < 1 || n > maxParcelas) {
      return `Escolha um número de parcelas entre 1 e ${maxParcelas}.`;
    }
  }
  if (isPhonePayment) {
    return validateMbwayPhone(mbwayPhone);
  }
  return null;
}

/**
 * Step 3 — Pagamento: descontos (Vales/Créditos/Promo) + método de pagamento +
 * resumo final. Confirmar redirecciona para a página da encomenda criada.
 */
export default function NewOrderPaymentPage() {
  const navigate = useNavigate();
  const { activeId } = useAuth();
  const [state, , reset] = useNewOrderState();

  const cartH = useCart();
  const addr = useAddresses();
  const pay = usePaymentMethods();

  const [showAddPhone, setShowAddPhone] = useState(false);
  const [newPhone, setNewPhone] = useState("");
  const [showDetails, setShowDetails] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [orderResult, setOrderResult] = useState(null);

  // Restaurar selecções de moradas/transporte do state persistido.
  useEffect(() => {
    if (state.selectedFatura && !addr.selectedFatura) addr.setSelectedFatura(state.selectedFatura);
    if (state.selectedEnvio && !addr.selectedEnvio) addr.setSelectedEnvio(state.selectedEnvio);
    if (state.selectedTransport && !pay.selectedTransport) pay.setSelectedTransport(state.selectedTransport);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addr.addresses.length, pay.transportMethods.length]);

  // Sem itens no carrinho → ao step 1; sem moradas/transporte → ao step 2.
  useEffect(() => {
    if (!cartH.loading && cartH.cart.length === 0) {
      navigate("/encomendas/nova/carrinho", { replace: true });
      return;
    }
    if (state.maxStep === "cart") {
      navigate("/encomendas/nova/checkout", { replace: true });
    }
  }, [cartH.loading, cartH.cart.length, state.maxStep, navigate]);

  // Recarregar quando muda cliente alvo.
  useEffect(() => {
    cartH.load?.();
    addr.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const disc = useDiscounts({
    selectedTransport: pay.selectedTransport,
    selectedPayment: pay.selectedPayment,
    selectedEnvio: addr.selectedEnvio,
  });
  const verify = useCheckoutVerify({
    cartLength: cartH.cart.length,
    selectedTransport: pay.selectedTransport,
    selectedPayment: pay.selectedPayment,
    selectedEnvio: addr.selectedEnvio,
    appliedCreditos: disc.appliedCreditos,
    appliedVales: disc.appliedVales,
    appliedPromo: disc.appliedPromo,
    balances: disc.balances,
  });

  const onChangePayment = (id) => {
    pay.setSelectedPayment(id);
    pay.setMbwayPhone("");
    pay.setParcelas(1);
    setShowAddPhone(false);
  };

  // Smart filter de métodos de pagamento (partilhado com MY) — esconde
  // Cofidis Pay para encomendas <60€/>2500€ ou NIFs de empresa, e respeita
  // `ValorMinimo` legacy. O array final mostra `_disabledReason` quando o
  // método aparece visível mas não é seleccionável (mínimo não atingido).
  const billingNif = useMemo(
    () => nifFromSelectedAddress(addr.addresses, addr.selectedFatura),
    [addr.addresses, addr.selectedFatura],
  );
  const smartPaymentMethods = useMemo(() => {
    const total = Number(verify.spTotalPagar ?? cartH.total ?? 0);
    return filterPaymentMethods(pay.selectablePaymentMethods, { total, billingNif })
      .map(({ method, rule }) => ({
        ...method,
        // O <option> nativo aceita disabled — usamos para regras `hidden:false`.
        _disabled: !!rule,
        _disabledReason: rule?.reason || '',
        Descricao: rule ? `${method.Descricao} — ${rule.reason}` : method.Descricao,
      }));
  }, [pay.selectablePaymentMethods, verify.spTotalPagar, cartH.total, billingNif]);

  const onSavePhone = async () => {
    const r = await pay.addPhone(newPhone);
    if (r.ok) {
      setNewPhone("");
      setShowAddPhone(false);
    } else if (r.message) {
      window.alert(r.message);
    }
  };

  const handleConfirm = async () => {
    // NIF da morada de facturação seleccionada — necessário para a regra
    // "Cofidis Pay não disponível para NIF empresa" (validateCheckout).
    const billingAddr = (addr.addresses || []).find((a) =>
      String(a.ID_ClienteMorada ?? a.ID_Cliente_Morada) === String(addr.selectedFatura));
    const billingNif = (billingAddr?.Nif || billingAddr?.NIF || "").toString().trim();

    const check = validateCheckout({
      verifyData: verify.spTotal,
      billingAddressId: addr.selectedFatura || addr.selectedEnvio,
      shippingAddressId: addr.selectedEnvio,
      deliveryMethod: pay.selectedTransport,
      deliveryMethods: pay.transportMethods,
      paymentMethod: pay.selectedPayment,
      paymentMethods: pay.paymentMethods,
      mbwayPhone: pay.mbwayPhone,
      cartEmpty: cartH.cart.length === 0,
      billingNif,
    });
    if (!check.ok) {
      window.alert(check.message);
      return;
    }
    const extraErr = validateExtraCheckout({
      isInstallments: pay.isInstallments,
      parcelas: pay.parcelas,
      maxParcelas: pay.maxParcelas,
      isPhonePayment: pay.isPhonePayment,
      mbwayPhone: pay.mbwayPhone,
    });
    if (extraErr) {
      window.alert(extraErr);
      return;
    }

    setConfirming(true);
    try {
      // Source of truth: usar os valores efectivamente aceites pelo SP via
      // `useCheckoutVerify` — equivalente a `acceptedVouchers/credits/promo`
      // do MY checkout. Evita mismatch quando o utilizador pede mais do que
      // o cap server-side.
      const acceptedVales = Number(verify.spDescontoVale || 0);
      const acceptedCreditos = Number(verify.spDescontoCreditos || 0);
      const acceptedPromoKey = verify.spTotal?.ChaveCodigoPromocional || disc.appliedPromo || "";

      const { data } = await api.post("/encomendas/confirm", {
        id_tipo_transporte: pay.selectedTransport,
        id_tipo_pagamento: pay.selectedPayment,
        id_morada_envio: addr.selectedEnvio,
        id_morada_fatura: addr.selectedFatura || addr.selectedEnvio,
        observacoes: state.observacoes || "",
        desconto_vale: acceptedVales,
        desconto_creditos: acceptedCreditos,
        codigo_promocional: acceptedPromoKey,
        id_portes: verify.spTotal?.ID_Portes || "",
        parcelas: pay.isInstallments ? pay.parcelas : 0,
        numero_telemovel: pay.isPhonePayment ? pay.mbwayPhone : "",
      });
      setOrderResult(data);
      if (data?.success && data?.id_encomenda) {
        // Reset state e redirect para a página da encomenda criada.
        reset();
        navigate(`/encomendas/${encodeURIComponent(data.id_encomenda)}`, { replace: true });
      }
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.warn("confirm failed", e);
      setOrderResult({ success: false, message: "Erro de comunicação." });
    } finally {
      setConfirming(false);
    }
  };

  if (orderResult?.success && !orderResult.id_encomenda) {
    return <OrderConfirmedScreen message={orderResult.message} onBack={() => navigate("/encomendas")} />;
  }

  return (
    <div data-testid="new-order-payment-page">
      <NewOrderHeader maxStepReached={maxStep(state.maxStep, "payment")} />

      <div className="mb-4">
        <h1 className="text-2xl font-bold text-[#111827]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 600 }}>Pagamento</h1>
        <p className="text-sm text-[#6B7280] mt-1">Aplique descontos, escolha o método de pagamento e confirme a encomenda.</p>
      </div>

      <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm p-5">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 space-y-4">
            <DiscountsSection
              promoCode={disc.promoCode} setPromoCode={disc.setPromoCode}
              appliedPromo={disc.appliedPromo} applyPromo={disc.applyPromo} removePromo={disc.removePromo}
              promoLoading={disc.promoLoading} promoError={disc.promoError}
              promoSuccess={disc.promoSuccess} setPromoError={disc.setPromoError}
              spTotal={verify.spTotal} spValorDescontoCP={verify.spValorDescontoCP}
              valesValue={disc.valesValue} setValesValue={disc.setValesValue}
              appliedVales={disc.appliedVales} applyVales={disc.applyVales} removeVales={disc.removeVales}
              spValesPermitidos={verify.spValesPermitidos} spValesExistentes={verify.spValesExistentes}
              spDescontoVale={verify.spDescontoVale}
              creditosValue={disc.creditosValue} setCreditosValue={disc.setCreditosValue}
              appliedCreditos={disc.appliedCreditos} applyCreditos={disc.applyCreditos}
              removeCreditos={disc.removeCreditos}
              spCreditosPermitidos={verify.spCreditosPermitidos}
              spCreditosExistentes={verify.spCreditosExistentes}
              spDescontoCreditos={verify.spDescontoCreditos}
            />
          </div>

          <div className="lg:col-span-5 space-y-4">
            <PaymentSection
              selectablePaymentMethods={smartPaymentMethods}
              selectedPayment={pay.selectedPayment} onChangePayment={onChangePayment}
              isInstallments={pay.isInstallments} parcelas={pay.parcelas}
              setParcelas={pay.setParcelas} maxParcelas={pay.maxParcelas}
              isPhonePayment={pay.isPhonePayment}
              phoneNumbers={pay.phoneNumbers} mbwayPhone={pay.mbwayPhone}
              setMbwayPhone={pay.setMbwayPhone}
              showAddPhone={showAddPhone} setShowAddPhone={setShowAddPhone}
              newPhone={newPhone} setNewPhone={setNewPhone} onSavePhone={onSavePhone}
            />

            <TotalBox
              showDetails={showDetails} setShowDetails={setShowDetails}
              spSubtotal={verify.spSubtotal} spPortes={verify.spPortes} spIva={verify.spIva}
              cartTotal={cartH.total}
              spValorDescontoCP={verify.spValorDescontoCP}
              spDescontoVale={verify.spDescontoVale}
              spDescontoCreditos={verify.spDescontoCreditos}
              spDescontos={verify.spDescontos}
              spTotalPagar={verify.spTotalPagar}
              verifying={verify.verifying}
              confirming={confirming} onConfirm={handleConfirm}
              isPhonePayment={pay.isPhonePayment} mbwayPhone={pay.mbwayPhone}
              selectedPayment={pay.selectedPayment} selectedTransport={pay.selectedTransport}
              selectedAddrEnvio={addr.selectedEnvio}
              orderResult={orderResult}
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm p-5 mt-4 flex items-center justify-between gap-4 flex-wrap">
        <button type="button" onClick={() => navigate("/encomendas/nova/checkout")}
          data-testid="new-order-payment-back"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-[#E5E7EB] text-[#374151] text-sm rounded-md hover:bg-[#F9FAFB] transition-colors">
          <ArrowLeft size={16} /> Voltar aos Dados
        </button>
        <div className="text-xs text-[#6B7280]">Use o botão "Confirmar encomenda" no resumo acima.</div>
      </div>
    </div>
  );
}
